import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Loader2, CheckCircle2, FileUp, AlertTriangle, RefreshCw } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

const ACTION_BADGE = {
  update: ['Wird aktualisiert', 'border-emerald-200 bg-emerald-50 text-emerald-700'],
  unchanged: ['Unverändert', 'border-slate-200 bg-slate-50 text-slate-500'],
  unmatched: ['Nicht zuordenbar', 'border-amber-200 bg-amber-50 text-amber-700'],
  invalid: ['Ungültig', 'border-rose-200 bg-rose-50 text-rose-600'],
};

export const SepaMandateImportTab = () => {
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [followup, setFollowup] = useState(null);
  const [assignRow, setAssignRow] = useState(null);
  const [gSearch, setGSearch] = useState('');
  const [gResults, setGResults] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [newPayer, setNewPayer] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [sSearch, setSSearch] = useState('');
  const [sResults, setSResults] = useState([]);
  const [linkStudent, setLinkStudent] = useState(null);

  const loadFollowup = useCallback(async () => {
    try {
      const r = await api.get('/sepa/mandates/followup');
      setFollowup(r.data);
    } catch { /* Nacharbeit optional */ }
  }, []);

  useEffect(() => { loadFollowup(); }, [loadFollowup]);

  useEffect(() => {
    if (!assignRow || gSearch.trim().length < 2) { setGResults([]); return; }
    const tm = setTimeout(async () => {
      try {
        const r = await api.get('/guardians', { params: { search: gSearch.trim(), limit: 10 } });
        setGResults(r.data || []);
      } catch { setGResults([]); }
    }, 300);
    return () => clearTimeout(tm);
  }, [gSearch, assignRow]);

  useEffect(() => {
    if (!assignRow || sSearch.trim().length < 2) { setSResults([]); return; }
    const tm = setTimeout(async () => {
      try {
        const r = await api.get('/students', { params: { search: sSearch.trim(), limit: 8 } });
        setSResults(r.data?.items || r.data || []);
      } catch { setSResults([]); }
    }, 300);
    return () => clearTimeout(tm);
  }, [sSearch, assignRow]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/sepa/mandates/import/preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Vorschau fehlgeschlagen');
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const r = await api.post('/sepa/mandates/import/commit', { token: preview.token });
      const nachgetragen = [
        r.data.kassenzeichen_neu ? `${r.data.kassenzeichen_neu} Kassenzeichen` : null,
        r.data.banknamen_neu ? `${r.data.banknamen_neu} Banknamen` : null,
      ].filter(Boolean);
      toast.success(`${r.data.updated} Mandate übernommen (${r.data.unmatched} nicht zuordenbar, ${r.data.invalid} ungültig)`
        + (nachgetragen.length ? `, ${nachgetragen.join(' und ')} nachgetragen` : ''));
      setPreview(null);
      loadFollowup();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Übernahme fehlgeschlagen');
    } finally {
      setCommitting(false);
    }
  };

  const openAssign = (row) => {
    setAssignRow(row);
    setGSearch(row.account_holder || '');
    setGResults([]);
    const parts = (row.account_holder || '').trim().split(/\s+/);
    setNewPayer({ first_name: parts.slice(1).join(' '), last_name: parts[0] || '', email: '', phone: '' });
    setSSearch('');
    setSResults([]);
    setLinkStudent(null);
  };

  const doAssign = async (guardianId) => {
    setAssigning(true);
    try {
      const r = await api.post('/sepa/mandates/unmatched/assign', { row_id: assignRow.id, guardian_id: guardianId });
      toast.success(`Mandat ${assignRow.kassenzeichen || ''} zugewiesen an ${r.data.guardian_name}`);
      setAssignRow(null);
      loadFollowup();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Zuweisung fehlgeschlagen');
    } finally {
      setAssigning(false);
    }
  };

  const doCreatePayer = async () => {
    setAssigning(true);
    try {
      const r = await api.post('/sepa/mandates/unmatched/create-payer', {
        row_id: assignRow.id,
        first_name: newPayer.first_name,
        last_name: newPayer.last_name,
        email: newPayer.email || null,
        phone: newPayer.phone || null,
        student_id: linkStudent?.id || null,
      });
      toast.success(`Zahler ${r.data.guardian_name} angelegt, Mandat zugewiesen${r.data.linked_student ? ` (verknüpft mit ${r.data.linked_student})` : ''}`);
      setAssignRow(null);
      loadFollowup();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Anlegen fehlgeschlagen');
    } finally {
      setAssigning(false);
    }
  };

  const doDismiss = async (row) => {
    try {
      await api.post('/sepa/mandates/unmatched/dismiss', { row_id: row.id });
      toast.success(`Zeile ${row.kassenzeichen || ''} verworfen`);
      loadFollowup();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verwerfen fehlgeschlagen');
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">SEPA-Mandate importieren (CSV oder XML)</CardTitle>
          <CardDescription>
            <b>CSV</b> (iMikel/Excel) mit Spalte <b>IBAN</b> (Pflicht) sowie optional Kassenzeichen,
            Kontoinhaber, BIC, Bank, Mandatsreferenz, Mandatsdatum, Nachname/Vorname — <b>oder</b> eine
            bestehende <b>SEPA-Lastschrift-XML (pain.008)</b>: daraus werden Kontoinhaber, IBAN, BIC,
            Mandatsreferenz und Unterschriftsdatum je Zahler übernommen. Zuordnung über das Kassenzeichen
            (bevorzugt) oder den Namen; die Bankdaten werden beim zahlenden Erziehungsberechtigten hinterlegt.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <input type="file" accept=".csv,.xml,text/csv,text/xml,application/xml" ref={fileRef} onChange={handleUpload}
                 className="hidden" data-testid="mandate-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={loading}
                  data-testid="mandate-upload-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileUp className="w-4 h-4 mr-2" />}
            CSV- oder XML-Datei auswählen (Vorschau)
          </Button>
        </CardContent>
      </Card>

      {preview && (
        <Card data-testid="mandate-preview">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-4">
              <div>
                <CardTitle className="text-base">Vorschau: {preview.rows.length} Zeilen</CardTitle>
                <CardDescription>
                  {Object.entries(preview.summary).map(([k, v]) => `${ACTION_BADGE[k]?.[0] || k}: ${v}`).join(' · ')}
                </CardDescription>
              </div>
              <Button onClick={commit} disabled={committing || !(preview.summary.update > 0)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="mandate-commit-btn">
                {committing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                {preview.summary.update || 0} Mandate übernehmen
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[420px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">CSV-Zeile</th>
                    <th className="text-left px-3 py-2">IBAN</th>
                    <th className="text-left px-3 py-2">Zugeordnet zu</th>
                    <th className="text-left px-3 py-2">Hinweis</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => {
                    const [label, cls] = ACTION_BADGE[r.action] || [r.action, ''];
                    return (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-3 py-1.5"><Badge variant="outline" className={`text-[10px] ${cls}`}>{label}</Badge></td>
                        <td className="px-3 py-1.5">{r.kassenzeichen && <span className="font-mono">{r.kassenzeichen} </span>}{r.name || r.account_holder || '—'}</td>
                        <td className="px-3 py-1.5 font-mono">{r.iban}</td>
                        <td className="px-3 py-1.5">{r.guardian_name || '—'}{r.family_name && <span className="text-slate-400"> ({r.family_name})</span>}</td>
                        <td className="px-3 py-1.5 text-slate-500">{r.reason || r.warning || (r.matched_by ? `via ${r.matched_by}` : '')}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {followup && (followup.mandate_problems.length > 0 || followup.unmatched_import.rows.length > 0) && (
        <Card data-testid="mandate-followup">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-4">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-brand-warning" /> Nacharbeit
                </CardTitle>
                <CardDescription>
                  {followup.mandate_problems.length} Mandate mit Problem · {followup.unmatched_import.rows.length} nicht
                  zuordenbare Zeilen aus dem letzten Import
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={loadFollowup} data-testid="mandate-followup-refresh">
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Aktualisieren
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {followup.mandate_problems.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-1.5">Erloschene / widerrufene / unvollständige Mandate — bitte neu einholen</p>
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-3 py-2">Zahler</th>
                      <th className="text-left px-3 py-2">Kontakt</th>
                      <th className="text-left px-3 py-2">IBAN</th>
                      <th className="text-left px-3 py-2">Mandatsref.</th>
                      <th className="text-left px-3 py-2">Letzte Nutzung</th>
                      <th className="text-left px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {followup.mandate_problems.map((p) => (
                      <tr key={p.guardian_id} className="border-t border-slate-100" data-testid={`followup-problem-${p.guardian_id}`}>
                        <td className="px-3 py-1.5 font-medium">{p.name || '—'}</td>
                        <td className="px-3 py-1.5 text-slate-500">{p.email || p.phone || '—'}</td>
                        <td className="px-3 py-1.5 font-mono">{p.iban_masked}</td>
                        <td className="px-3 py-1.5 font-mono">{p.mandate_reference || '—'}</td>
                        <td className="px-3 py-1.5">{p.last_used_at || (p.mandate_date ? `unterschrieben ${p.mandate_date}` : '—')}</td>
                        <td className="px-3 py-1.5"><Badge variant="outline" className="text-[10px] border-rose-200 bg-rose-50 text-rose-600">{p.status_label}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {followup.unmatched_import.rows.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-1.5">
                  Nicht zuordenbare Zeilen aus dem letzten Import — Kassenzeichen unbekannt (vermutlich noch nicht angelegte Schüler/Familien)
                </p>
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-3 py-2">Kassenzeichen</th>
                      <th className="text-left px-3 py-2">Kontoinhaber</th>
                      <th className="text-left px-3 py-2">IBAN</th>
                      <th className="text-left px-3 py-2">Mandatsdatum</th>
                      <th className="text-left px-3 py-2">Grund</th>
                      <th className="text-right px-3 py-2">Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {followup.unmatched_import.rows.map((u, i) => (
                      <tr key={u.id || i} className="border-t border-slate-100" data-testid={`followup-unmatched-${i}`}>
                        <td className="px-3 py-1.5 font-mono">{u.kassenzeichen || '—'}</td>
                        <td className="px-3 py-1.5">{u.account_holder || '—'}</td>
                        <td className="px-3 py-1.5 font-mono">{u.iban_masked}</td>
                        <td className="px-3 py-1.5">{u.mandate_date || '—'}</td>
                        <td className="px-3 py-1.5 text-slate-500">{u.reason || '—'}</td>
                        <td className="px-3 py-1.5 text-right whitespace-nowrap">
                          <Button variant="outline" size="sm" className="h-6 px-2 text-[11px] mr-1.5"
                                  onClick={() => openAssign(u)} data-testid={`followup-assign-${i}`}>
                            Zuordnen
                          </Button>
                          <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px] text-slate-400 hover:text-rose-600"
                                  onClick={() => doDismiss(u)} data-testid={`followup-dismiss-${i}`}>
                            Verwerfen
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={!!assignRow} onOpenChange={(o) => !o && setAssignRow(null)}>
        <DialogContent className="max-w-lg" data-testid="assign-mandate-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Mandat manuell zuordnen</DialogTitle>
            <DialogDescription>
              {assignRow && (
                <span className="font-mono text-xs">
                  {assignRow.kassenzeichen || 'ohne KZ'} · {assignRow.iban_masked}
                  {assignRow.account_holder ? ` · ${assignRow.account_holder}` : ''}
                  {assignRow.mandate_date ? ` · Mandat vom ${assignRow.mandate_date}` : ''}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Input placeholder="Zahler suchen (Name, E-Mail) …" value={gSearch}
                   onChange={(e) => setGSearch(e.target.value)} autoFocus
                   data-testid="assign-guardian-search" />
            <div className="max-h-64 overflow-y-auto divide-y divide-slate-100 rounded border border-slate-100">
              {gResults.map((g) => (
                <button key={g.id} disabled={assigning}
                        onClick={() => doAssign(g.id)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center justify-between gap-2"
                        data-testid={`assign-guardian-option-${g.id}`}>
                  <span>
                    <span className="font-medium">{`${g.first_name || ''} ${g.last_name || ''}`.trim() || '—'}</span>
                    {g.email && <span className="text-slate-400 ml-2 text-xs">{g.email}</span>}
                    {g.student_names?.length > 0 && (
                      <span className="block text-[11px] text-slate-400">Schüler: {g.student_names.join(', ')}</span>
                    )}
                  </span>
                  {g.bank_iban ? (
                    <Badge variant="outline" className="text-[10px] border-amber-200 bg-amber-50 text-amber-700">hat bereits IBAN</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] border-slate-200 bg-slate-50 text-slate-500">keine IBAN</Badge>
                  )}
                </button>
              ))}
              {gSearch.trim().length >= 2 && gResults.length === 0 && (
                <p className="px-3 py-3 text-xs text-slate-400">Keine Treffer — Zahler ggf. zuerst bei der Familie anlegen.</p>
              )}
            </div>
            <p className="text-[11px] text-slate-400">
              Die Bankdaten (IBAN, BIC, Kontoinhaber, Mandatsreferenz/-datum) werden beim gewählten Zahler hinterlegt
              und die Zeile aus der Nacharbeits-Liste entfernt.
            </p>

            <div className="flex items-center gap-3 pt-1">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="text-[11px] uppercase tracking-wide text-slate-400">oder neuen Zahler anlegen</span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Vorname" value={newPayer.first_name}
                     onChange={(e) => setNewPayer({ ...newPayer, first_name: e.target.value })}
                     data-testid="new-payer-first-name" />
              <Input placeholder="Nachname *" value={newPayer.last_name}
                     onChange={(e) => setNewPayer({ ...newPayer, last_name: e.target.value })}
                     data-testid="new-payer-last-name" />
              <Input placeholder="E-Mail (optional)" value={newPayer.email}
                     onChange={(e) => setNewPayer({ ...newPayer, email: e.target.value })}
                     data-testid="new-payer-email" />
              <Input placeholder="Telefon (optional)" value={newPayer.phone}
                     onChange={(e) => setNewPayer({ ...newPayer, phone: e.target.value })}
                     data-testid="new-payer-phone" />
            </div>
            {linkStudent ? (
              <div className="flex items-center gap-2 text-xs">
                <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                  Schüler: {`${linkStudent.first_name || ''} ${linkStudent.last_name || ''}`.trim()}
                </Badge>
                <button className="text-slate-400 hover:text-rose-600" onClick={() => setLinkStudent(null)}
                        data-testid="new-payer-unlink-student">entfernen</button>
              </div>
            ) : (
              <div>
                <Input placeholder="Schüler verknüpfen (optional, Name suchen) …" value={sSearch}
                       onChange={(e) => setSSearch(e.target.value)} data-testid="new-payer-student-search" />
                {sResults.length > 0 && (
                  <div className="max-h-36 overflow-y-auto divide-y divide-slate-100 rounded border border-slate-100 mt-1">
                    {sResults.map((s) => (
                      <button key={s.id} onClick={() => { setLinkStudent(s); setSSearch(''); setSResults([]); }}
                              className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50"
                              data-testid={`new-payer-student-option-${s.id}`}>
                        {`${s.first_name || ''} ${s.last_name || ''}`.trim()}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <Button onClick={doCreatePayer} disabled={assigning || !newPayer.last_name.trim()}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="new-payer-create-btn">
              {assigning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Zahler anlegen & Mandat zuweisen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SepaMandateImportTab;
